import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { safeStorage } from '@/lib/safe-storage';

// ─── Theme: light (the default) or dark ───────────────────────────────────────
// Two choices, not three. This used to cycle dark → light → system, with no
// control anywhere that called it, so nobody could reach any of them. Light
// became the default on 2026-10-21 (it was dark); whoever picks one with the
// toggle keeps it.
//
// Only a choice is stored, under THEME_STORAGE_KEY. The old key, `bl-theme`,
// was written on every load whatever the visitor did, so nearly everyone had
// `dark` saved without choosing it, and honouring that would have kept almost
// every returning visitor on dark after the switch. A `light` under the old
// key could only come from the toggle, so that one still counts.
//
// To change the default: DEFAULT_THEME below, and DEFAULT_THEME in the script
// in index.html. ThemeToggle.test.jsx fails if the two disagree.
//
// The class goes on <html>, where the palette in src/index.css reads it:
// `dark` keeps Tailwind's `dark:` variant working, `light` flips the palette.
// index.html applies the stored choice before first paint with the same key, so
// the page never flashes the wrong theme — keep the two in step.

export const THEME_STORAGE_KEY = 'bl-theme-choice';
export const LEGACY_THEME_KEY = 'bl-theme';
/** What a visitor sees until they choose: 'light' or 'dark'. */
export const DEFAULT_THEME = /** @type {'light' | 'dark'} */ ('light');

// Browser chrome (the address bar on Android, the status area on iOS) follows
// the page background rather than staying dark over a light page.
const CHROME_COLOR = { dark: '#0b0b0c', light: '#f3f3f1' };

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  isDark: DEFAULT_THEME === 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

/** A stored 'light' or 'dark' is the visitor's choice; anything else —
 *  nothing, or the retired 'system' — means the default. */
function normalise(value) {
  return value === 'light' || value === 'dark' ? value : DEFAULT_THEME;
}

function storedTheme() {
  const chosen = safeStorage.getItem(THEME_STORAGE_KEY);
  if (chosen === 'light' || chosen === 'dark') return chosen;
  return safeStorage.getItem(LEGACY_THEME_KEY) === 'light' ? 'light' : DEFAULT_THEME;
}

// ThemeProvider wraps the entire app, so anything that throws in here takes the
// whole page down to a blank screen before React can render a fallback. Storage
// goes through safeStorage and every DOM touch is guarded.
function applyTheme(theme) {
  try {
    const root = document.documentElement;
    const dark = theme !== 'light';
    root.classList.toggle('dark', dark);
    root.classList.toggle('light', !dark);
    // Native controls — scrollbars, date pickers, autofill — follow this.
    root.style.colorScheme = dark ? 'dark' : 'light';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', CHROME_COLOR[dark ? 'dark' : 'light']);
  } catch { /* a theme that cannot apply must not take the page down */ }
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(storedTheme);

  // Applied on every change, but stored only by the two choosers below, so
  // the default can still change for anyone who never picked.
  useEffect(() => { applyTheme(theme); }, [theme]);

  // A choice made in another tab applies here too, rather than the two tabs
  // disagreeing until one is reloaded.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === THEME_STORAGE_KEY) setThemeState(normalise(e.newValue));
    };
    try { window.addEventListener('storage', onStorage); } catch { return undefined; }
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = useCallback((next) => {
    const chosen = normalise(next);
    safeStorage.setItem(THEME_STORAGE_KEY, chosen);
    setThemeState(chosen);
  }, []);
  const toggleTheme = useCallback(() => {
    setThemeState((t) => {
      const chosen = t === 'light' ? 'dark' : 'light';
      safeStorage.setItem(THEME_STORAGE_KEY, chosen);
      return chosen;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, isDark: theme !== 'light', setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
